import { track } from '@bnnvara/analytics/src/track';
import { sizes } from '@bnnvara/breakpoints';
import { Chevron } from '@bnnvara/icons/src/components/UserInterfaceIcons/Chevron';
import { Subscribe } from '@bnnvara/icons/src/components/UserInterfaceIcons/Subscribe';
import fetchJsonp from 'fetch-jsonp';
import debounce from 'lodash/debounce';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Spring, Trail, Transition } from 'react-spring';

import MenuButton from './MenuButton';
import * as S from './styled';
import SubMenu from './SubMenu';

const ResponsiveSpring = ({ children, open }) => {
  if (!process.browser) {
    return open && children;
  }

  const isDesktop = !process.browser || window.innerWidth >= sizes.md;
  const { innerHeight } = window;
  const responsiveHeight = 2.75;

  let from = { height: '0px' };
  let leave = { ...from };
  let enter = { height: `${innerHeight / responsiveHeight}px` };

  if (isDesktop) {
    from = { opacity: 0, transform: 'translateX(1vw)' };
    leave = { ...from, transform: 'translateX(-1vw)' };
    enter = { opacity: 1, transform: 'translateX(0)' };
  }

  return (
    <Transition native items={open} from={from} enter={enter} leave={leave}>
      {(toggle) => toggle && children}
    </Transition>
  );
};

ResponsiveSpring.propTypes = {
  children: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

class Menu extends Component {
  state = {
    selectedTab: null,
    desktop: true,
    leden: [],
    services: [],
  };

  constructor(props) {
    super(props);

    this.handleResizeDebounced = debounce(this.handleResize, 300);
  }

  componentDidMount() {
    const desktop = window.innerWidth >= sizes.md;

    this.setState({
      selectedTab: desktop ? "programma's" : null,
      desktop,
    });

    this.loadContent();

    window.addEventListener('resize', this.handleResizeDebounced);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResizeDebounced);
  }

  handleResize = () => {
    const desktop = window.innerWidth >= sizes.md;

    this.setState({
      desktop,
    });
  };

  selectTab = (tab) => {
    const { desktop, selectedTab } = this.state;
    track('bnnvara-menu', 'click', `tab-${tab}`);
    // On desktop tap will always remain open to prevent empty space
    if (desktop && tab === selectedTab) {
      return;
    }

    // On mobile you can open a close a tab
    this.setState({
      selectedTab: tab === selectedTab ? null : tab,
    });
  };

  loadContentSection = async (url) => {
    try {
      const response = await fetchJsonp(url, {
        jsonpCallbackFunction: 'jsonCallback',
      });

      return response.json();
    } catch (err) {
      return [];
    }
  };

  async loadContent() {
    const services = await this.loadContentSection(
      'https://assets.bnnvara.nl/json/services.json'
    );
    const leden = await this.loadContentSection(
      'https://assets.bnnvara.nl/json/leden.json'
    );

    this.setState({
      services,
      leden,
    });
  }

  render() {
    const { open } = this.props;
    const { selectedTab, services, leden, desktop } = this.state;

    const menu = [
      {
        label: 'Leden',
        children: leden,
      },
      {
        label: 'Service',
        children: services,
      },
    ];

    const { leftCol, rightCol } = menu.reduce(
      (initialValue, { intro, header, label, children }) => {
        initialValue.leftCol.push(
          <S.TabsButton
            key={label}
            selected={selectedTab === label}
            variant="menu"
            onClick={() => this.selectTab(label)}
          >
            <S.LabelWrapper>
              {label} <Chevron />
            </S.LabelWrapper>
          </S.TabsButton>
        );

        const submenu = (
          <ResponsiveSpring
            label={label}
            key={`submenu-${label}`}
            open={selectedTab === label}
          >
            {(animation) => (
              <SubMenu
                items={children}
                header={header}
                style={{
                  ...animation,
                }}
              >
                {intro}
              </SubMenu>
            )}
          </ResponsiveSpring>
        );

        if (!desktop) {
          initialValue.leftCol.push(submenu);
        } else {
          initialValue.rightCol.push(submenu);
        }

        return initialValue;
      },
      {
        leftCol: [<MenuButton />],
        rightCol: [],
      }
    );

    const reverse = !open;
    const from = { opacity: 0, transform: 'translateX(-2vw)' };
    const to = { opacity: 1, transform: 'translateX(0)' };

    return (
      <S.Menu>
        <S.BlockOne>
          <S.TopContent>
            <Trail
              native
              reverse={reverse}
              items={leftCol}
              keys={(item) => item.key}
              from={reverse ? to : from}
              to={reverse ? from : to}
            >
              {(item) => (props) => (
                <S.MenuItemContainer style={props}>{item}</S.MenuItemContainer>
              )}
            </Trail>
          </S.TopContent>
          <Spring from={open ? from : to} to={open ? to : from}>
            {(style) => (
              <div style={style}>
                <Link href="/sluit-je-aan">
                  <S.SubscribeButton
                    key="subscribe"
                    type="link"
                    variant="underline"
                  >
                    Sluit je aan bij BNNVARA! <Subscribe />
                  </S.SubscribeButton>
                </Link>
              </div>
            )}
          </Spring>
        </S.BlockOne>
        {rightCol.length ? <S.BlockTwo>{rightCol}</S.BlockTwo> : null}
      </S.Menu>
    );
  }
}

Menu.propTypes = {
  open: PropTypes.bool,
};

Menu.defaultProps = {
  open: false,
};

export default Menu;
